import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { translateLeagueBreadcrumb } from '@/i18n/sports-label';
import { cn } from '@/lib/cn';
import { isFlagBadgeSrc } from '@/shell/sports-menu-utils';
import { Icon } from '../common/icon';

export interface EventDetailNavProps {
  /** Fallback breadcrumb when category/tournament names are unavailable. */
  league: string;
  /** Display category name from the event payload (SportsTranslates). */
  categoryName?: string | null;
  categoryId?: number | null;
  /** Display tournament name from the event payload (SportsTranslates). */
  tournamentName?: string | null;
  tournamentId?: number | null;
  crestSrc?: string;
  /** Route slug params for the back link. */
  sport: string;
  category: string;
  tournament: string;
  className?: string;
}

/** Back chevron + league breadcrumb from the Figma Event Detail frame. */
export function EventDetailNav({
  league,
  categoryName,
  categoryId,
  tournamentName,
  tournamentId,
  crestSrc,
  sport,
  category,
  tournament,
  className,
}: EventDetailNavProps) {
  const { t, i18n } = useTranslation();
  const leagueLabel =
    categoryName || tournamentName
      ? translateLeagueBreadcrumb(
          i18n,
          t,
          {
            category: categoryName,
            categoryId,
            tournament: tournamentName,
            tournamentId,
          },
          ' > ',
        ) || league
      : league;

  return (
    <nav
      className={cn('flex items-center gap-2', className)}
      aria-label={t('Event', { defaultValue: 'Event' })}
    >
      <Link
        to="/sports/$sport/$category/$tournament"
        params={{ sport, category, tournament }}
        aria-label={t('Back to tournament', { defaultValue: 'Back to tournament' })}
        className="flex size-[18px] shrink-0 items-center justify-center text-icon-default"
      >
        <Icon name="arrow-right" size={18} className="-scale-x-100" />
      </Link>
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {crestSrc ? (
          <span
            className={cn(
              'flex size-[18px] shrink-0 items-center justify-center overflow-clip rounded-full',
              !isFlagBadgeSrc(crestSrc) && 'bg-white',
            )}
          >
            <img
              src={crestSrc}
              alt=""
              className={
                isFlagBadgeSrc(crestSrc) ? 'size-full object-cover' : 'size-3.5 object-contain'
              }
            />
          </span>
        ) : null}
        <p className="truncate text-caption text-text-primary">{leagueLabel}</p>
      </div>
    </nav>
  );
}
